<template>
    <form-group
        v-bind="form_group_props"
        class="form-image-input"
    >
        <template v-slot:read_only>
            <slot name="read_only">
                <div class="form-image-input-empty uk-flex uk-flex-column uk-flex-center uk-flex-middle uk-text-center">
                    <img v-if="value" :src="value" />
                    <div v-else-if="!!glyph">
                        <open-icon :glyph="glyph" />
                    </div>
                </div>
            </slot>
        </template>
        <template v-slot:label><slot name="label"></slot></template>
        <template v-slot:hint><slot name="hint"></slot></template>
        <div
            ref="dazone"
            :class="{ 'form-image-input-droppable': dragging, 'form-image-input-active': !!value }"
            class="uk-placeholder uk-flex uk-flex-column uk-flex-center uk-flex-middle uk-text-center form-image-input-dropzone"
            @click.prevent.stop="$emit('click')"
        >
            <img v-if="value" :src="value" />
            <div v-else>
                <open-icon :glyph="placeholderGlyph" size="medium" />
                <p v-if="!!placeholder" class="uk-text-small">{{ placeholder }}</p>
            </div>
        </div>
        <a
            v-if="editable && !!value"
            href
            class="uk-icon-button uk-icon-close"
            title="Remove Image"
            @click.prevent="$emit('remove')"
        ></a>
    </form-group>
</template>

<script>
import is_nibnut_component from "../mixins/IsNibnutComponent"

import FormGroup from "./FormGroup"
import OpenIcon from "./OpenIcon"

const event_prevent_and_stop = event => {
    event.preventDefault()
    event.stopPropagation()
}

export default {
    name: "FormImageInput",
    mixins: [is_nibnut_component],
    components: {
        FormGroup,
        OpenIcon
    },
    mounted () {
        const hookup = () => {
            if(!this.$refs.dazone) setTimeout(hookup, 100)
            else {
                ["drag", "dragstart", "dragend", "dragover"].forEach(event_name => {
                    this.$refs.dazone.addEventListener(event_name, event_prevent_and_stop)
                })

                this.$refs.dazone.addEventListener("dragenter", this.drag_over)
                this.$refs.dazone.addEventListener("dragleave", this.drag_out)
                this.$refs.dazone.addEventListener("drop", this.drop)
            }
        }
        hookup()
        this.dragging = false
    },
    beforeDestroy () {
        try {
            ["drag", "dragstart", "dragend", "dragover"].forEach(event_name => {
                if(this.$refs.dazone) this.$refs.dazone.removeEventListener(event_name, event_prevent_and_stop)
            })

            if(this.$refs.dazone) {
                this.$refs.dazone.removeEventListener("dragenter", this.drag_over)
                this.$refs.dazone.removeEventListener("dragleave", this.drag_out)
                this.$refs.dazone.removeEventListener("drop", this.drop)
            }
        } catch (exception) {}
    },
    methods: {
        drag_over (event) {
            event_prevent_and_stop(event)
            if(!this.editable) return
            this.dragging = true
        },
        drag_out (event) {
            event_prevent_and_stop(event)
            if(!this.editable) return
            this.dragging = false
        },
        drop (event) {
            event_prevent_and_stop(event)
            if(!this.editable) return
            this.upload(event.dataTransfer.files)
            this.dragging = false
        },
        upload (file_list) {
            if(!this.editable || !file_list || !file_list.length) return
            this.$emit("upload", file_list)
        }
    },
    computed: {
        form_group_props () {
            return {
                id: this.id,
                name: this.name,
                value: this.value,
                required: this.required,
                editable: this.editable,
                error: this.error,
                waiting: this.saving
            }
        }
    },
    props: {
        id: {
            type: String,
            validator: prop => !!prop
        },
        name: {
            type: String,
            validator: prop => !!prop,
            required: true
        },
        value: {
            default: ""
        },
        glyph: {
            type: String,
            default: "image"
        },
        required: {
            type: Boolean,
            required: true
        },
        placeholderGlyph: {
            type: String,
            default: "cloud-upload"
        },
        placeholder: {
            type: String,
            default: "...or click to import from a URL"
        },
        editable: {
            type: Boolean,
            default: true
        },
        saving: {
            type: Boolean,
            default: false
        },
        error: {
            validator: prop => (typeof prop === "boolean") || (typeof prop === "string"),
            default: ""
        }
    },
    data () {
        return {
            dragging: false
        }
    }
}
</script>

<style lang="less">
@import "@/assets/less/variables";

.nibnut-aspect-ratio-box .form-image-input {
    &, .form-image-input-empty, .uk-form-controls, .uk-form-controls > div {
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        margin: 0;
    }
}
.form-image-input {
    position: relative;

    .form-image-input-empty {
        background: lighten(@global-lam-daylight, 10%);
        border-color: @global-lam-gray;
    }

    .form-image-input-dropzone {
        cursor: pointer;
        padding: 0;
        & > * {
            pointer-events: none;
        }
        & > div {
            margin: 20px;
        }

        &.form-image-input-active {
            border-color: white;
            border-style: solid;
        }
        &.form-image-input-droppable {
            cursor: copy;
            background: @global-lam-sunrise;
            border-color: @global-lam-orange;
        }
    }
    .uk-icon-close {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 10;
    }
}
</style>
